/**
 * 公告管理（07 功能 PRD §3.2）：列表（状态筛选）→ 编辑（双语标题/摘要/正文、eraKey、
 * tone 纸色、publishAt 定时发布）→ 发布/下线。
 * 正文受限富文本（Markdown 子集），服务端净化后输出。
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { AdminNoticeDetail } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { Button } from '@chronospeak/ui';
import { ErrorNote, Loading, PageHeader, StatusBadge, TableWrap } from '../components/ui-bits';
import { TranslateButton, MachineDraftBadge } from '../components/TranslateButton';
import { RestrictedMdEditor } from '../components/RestrictedMdEditor';
import { useSession } from '../lib/session';

/**
 * 公告正文按段落拆分翻译（06 实现说明 §2.4：不把整段富文本塞给翻译 API——
 * Markdown 标签会被打乱；受限格式初稿语义保内容，人工补格式）。
 */
function splitBodyParas(body: string): string[] {
  // 对齐围栏块（2026-09-27）整块作为一段进翻译源——拆开会把 ::: 标记行单独送去
  // 翻译（浪费且可能被机翻译改写语法）。围栏块在翻译源中形如 "::: right\n…\n:::"。
  const out: string[] = [];
  let fence: string[] | null = null;
  for (const raw of body.split(/\n{2,}/)) {
    const seg = raw.trim();
    if (!seg) continue;
    if (fence !== null) {
      fence.push(seg);
      if (seg === ':::') {
        out.push(fence.join('\n\n'));
        fence = null;
      }
      continue;
    }
    if (/^:::\s*(center|right)$/.test(seg)) {
      fence = [seg];
      continue;
    }
    out.push(seg);
  }
  if (fence) out.push(fence.join('\n\n'));
  return out.length ? out : [body];
}

const TONES = [
  { value: 'cream', label: 'cream · 米白' },
  { value: 'manila', label: 'manila · 牛皮纸' },
  { value: 'grey', label: 'grey · 灰纸' },
];

/** era 白名单与 M1 era-rules 冻结一致（后台仅可选，不留空——自动推导由 API 兜底） */
const ERA_KEYS = [
  { value: 'founding', label: 'founding · 开镇（2026 上半年）' },
  { value: 'now', label: 'now · 现在（2026 夏起）' },
];

/* ---------------- 列表 ---------------- */

interface AdminNoticeSummaryRow {
  id: string;
  slug: string;
  title: string;
  status: string;
  eraKey: string | null;
  tone: string;
  publishAt: string | null;
  welcome: boolean;
  updatedAt: string;
}

export function NoticesListPage() {
  const [items, setItems] = useState<AdminNoticeSummaryRow[] | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminHttp.get<{ items: AdminNoticeSummaryRow[] }>(
        `/admin/notices?pageSize=100${status ? `&status=${status}` : ''}`,
      );
      setItems(res.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await adminHttp.post<AdminNoticeDetail>('/admin/notices', {
        slug: `notice-${Date.now().toString(36)}`,
        translations: { 'zh-CN': { title: '新公告', excerpt: '', body: '' } },
        eraKey: 'now',
        tone: 'cream',
        publishAt: new Date().toISOString(),
      });
      navigate(`/notices/${created.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '创建失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <PageHeader
        title='公告管理'
        sub='发布后按时期出现在公告栏板面；定时发布到点自动可见。'
        actions={
          <>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
              状态{' '}
              <select value={status} onChange={(e) => setStatus(e.target.value)} className='mgr-select'>
                <option value=''>全部</option>
                <option value='DRAFT'>草稿</option>
                <option value='PUBLISHED'>已发布</option>
                <option value='OFFLINE'>已下线</option>
              </select>
            </label>
            <Button variant='primary' size='md' onClick={() => void create()} disabled={busy}>
              新建公告
            </Button>
          </>
        }
      />
      {error && <ErrorNote message={error} />}
      {!items && !error && <Loading />}
      {items && (
        <TableWrap>
          <thead>
            <tr>
              <th scope='col'>时期</th>
              <th scope='col'>纸色</th>
              <th scope='col'>标题</th>
              <th scope='col'>发布时间</th>
              <th scope='col'>状态</th>
              <th scope='col' />
            </tr>
          </thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.id}>
                <td>
                  <code>{n.eraKey ?? '（自动）'}</code>
                </td>
                <td>{n.tone}</td>
                <td>
                  {n.title}
                  {n.welcome && (
                    <span
                      style={{
                        marginLeft: 8,
                        padding: '1px 8px',
                        border: '1px solid var(--gold-dim)',
                        borderRadius: 999,
                        fontSize: 11,
                        color: 'var(--gold-bright)',
                      }}
                    >
                      欢迎信
                    </span>
                  )}
                </td>
                <td>{n.publishAt ? new Date(n.publishAt).toLocaleString('zh-CN') : '—'}</td>
                <td>
                  <StatusBadge status={n.status} />
                </td>
                <td>
                  <Button variant='ghost' size='sm' onClick={() => navigate(`/notices/${n.id}`)}>
                    编辑
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </section>
  );
}

/* ---------------- 编辑 ---------------- */

interface NoticeForm {
  slug: string;
  zh: { title: string; excerpt: string; body: string };
  en: { title: string; excerpt: string; body: string };
  hasEn: boolean;
  eraKey: string;
  tone: string;
  publishAt: string;
  /** 首访欢迎信（2026-09-25）：同时至多一条，冲突 422 行内提示 */
  welcome: boolean;
  status: string;
}

export function NoticeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { admin } = useSession();
  const [form, setForm] = useState<NoticeForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** 「机翻初稿」脏标记：机翻填充置 true，任一英文字段编辑后清 false */
  const [mtDraft, setMtDraft] = useState(false);

  useEffect(() => {
    adminHttp
      .get<AdminNoticeDetail>(`/admin/notices/${id}`)
      .then((d) =>
        setForm({
          slug: d.slug,
          zh: d.translations['zh-CN'],
          en: d.translations.en ?? { title: '', excerpt: '', body: '' },
          hasEn: Boolean(d.translations.en),
          eraKey: d.eraKey ?? '',
          tone: d.tone,
          publishAt: d.publishAt ? new Date(d.publishAt).toISOString().slice(0, 16) : '',
          welcome: d.welcomeOnFirstVisit ?? false,
          status: d.status,
        }),
      )
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'));
  }, [id]);

  if (error && !form) {
    return (
      <section>
        <PageHeader title='编辑公告' />
        <ErrorNote message={error} />
      </section>
    );
  }
  if (!form) {
    return (
      <section>
        <PageHeader title='编辑公告' />
        <Loading />
      </section>
    );
  }

  const patch = (p: Partial<NoticeForm>) => setForm({ ...form, ...p });

  const save = async () => {
    setError(null);
    setNotice(null);
    if (!form.zh.title.trim()) return setError('标题（中文）必填。');
    if (!form.publishAt) return setError('发布时间必填（定时发布依据）。');
    try {
      await adminHttp.patch<AdminNoticeDetail>(`/admin/notices/${id}`, {
        slug: form.slug.trim(),
        translations: {
          'zh-CN': form.zh,
          ...(form.hasEn && form.en.title.trim() ? { en: form.en } : {}),
        },
        eraKey: form.eraKey,
        tone: form.tone,
        publishAt: new Date(form.publishAt).toISOString(),
        welcomeOnFirstVisit: form.welcome,
      });
      setNotice('已保存。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '保存失败');
    }
  };

  const doPublish = async (action: 'publish' | 'unpublish') => {
    setError(null);
    try {
      const res = await adminHttp.post<{ status: string }>(`/admin/notices/${id}/${action}`);
      patch({ status: res.status });
      setNotice(action === 'publish' ? '已发布。' : '已下线。');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '操作失败');
    }
  };

  const doDelete = async () => {
    if (!window.confirm('删除草稿不可恢复，确定？')) return;
    try {
      await adminHttp.delete(`/admin/notices/${id}`);
      navigate('/notices');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '删除失败（已发布请走下线）');
    }
  };

  const published = form.status === 'PUBLISHED';

  return (
    <section>
      <PageHeader
        title='编辑公告'
        sub={<>状态 <StatusBadge status={form.status} /> · slug <code>{form.slug}</code></>}
        actions={
          <>
            {form.status === 'DRAFT' && (
              <Button variant='ghost' size='sm' onClick={() => void doDelete()}>
                删除草稿
              </Button>
            )}
            {published ? (
              <Button variant='ghost' size='sm' onClick={() => void doPublish('unpublish')}>
                下线
              </Button>
            ) : (
              <Button variant='primary' size='sm' onClick={() => void doPublish('publish')}>
                发布
              </Button>
            )}
            <Button variant='primary' size='sm' onClick={() => void save()}>
              保存
            </Button>
          </>
        }
      />
      {error && <ErrorNote message={error} />}
      {notice && (
        <p role='status' className='mgr-notice'>
          {notice}
        </p>
      )}

      <div className='mgr-form-grid'>
        <fieldset className='mgr-fieldset'>
          <legend>中文（必填）</legend>
          <label className='mgr-field'>
            标题 *<input value={form.zh.title} onChange={(e) => patch({ zh: { ...form.zh, title: e.target.value } })} />
          </label>
          <label className='mgr-field'>
            摘要（板面纸张上的一段话）
            <textarea rows={3} value={form.zh.excerpt} onChange={(e) => patch({ zh: { ...form.zh, excerpt: e.target.value } })} />
          </label>
          <div className='mgr-field'>
            正文（受限 Markdown：段落/换行/**加粗**/*斜体*/[链接](https://…)；工具栏对选区生效；服务端净化）
            <RestrictedMdEditor
              value={form.zh.body}
              onChange={(next) => patch({ zh: { ...form.zh, body: next } })}
              placeholder={'支持换行与基础格式：\n**加粗** · *斜体* · [链接文字](https://…)\n空行分段；对齐：选中段落后点工具栏居中/右对齐（落款用）'}
              ariaLabel='中文正文编辑器'
            />
          </div>
        </fieldset>
        <fieldset className='mgr-fieldset'>
          <legend>
            <label style={{ display: 'inline', gap: 6 }}>
              <input type='checkbox' checked={form.hasEn} onChange={(e) => patch({ hasEn: e.target.checked })} /> 英文
            </label>
            <MachineDraftBadge visible={mtDraft} />
          </legend>
          {form.hasEn && (
            <>
              <div style={{ marginBottom: 12 }}>
                <TranslateButton
                  mtEnabled={admin?.mtEnabled ?? false}
                  collectSources={() => [
                    form.zh.title,
                    form.zh.excerpt,
                    ...splitBodyParas(form.zh.body),
                  ]}
                  onBeforeApply={() =>
                    [form.en.title, form.en.excerpt, form.en.body].every((v) => !v.trim()) ||
                    window.confirm('英文区已有内容，确定用机器翻译结果覆盖？（正文按段落重组）')
                  }
                  onApply={(translated) => {
                    // 前 2 段 = title/excerpt；其余 = body 段落（按空行双换行重组）
                    const [title = '', excerpt = '', ...bodyParas] = translated;
                    patch({ en: { title, excerpt, body: bodyParas.join('\n\n') } });
                    setMtDraft(true);
                  }}
                />
              </div>
              <label className='mgr-field'>
                Title<input value={form.en.title} onChange={(e) => { patch({ en: { ...form.en, title: e.target.value } }); setMtDraft(false); }} />
              </label>
              <label className='mgr-field'>
                Excerpt
                <textarea rows={3} value={form.en.excerpt} onChange={(e) => { patch({ en: { ...form.en, excerpt: e.target.value } }); setMtDraft(false); }} />
              </label>
              <div className='mgr-field'>
                Body（line breaks, **bold**, *italic*, [link](https://…); toolbar acts on selection）
                <RestrictedMdEditor
                  value={form.en.body}
                  onChange={(next) => { patch({ en: { ...form.en, body: next } }); setMtDraft(false); }}
                  placeholder={'Line breaks + basic format:\n**bold** · *italic* · [link text](https://…)\nBlank line starts a new paragraph; select text then use align buttons (right for sign-off).'}
                  ariaLabel='英文正文编辑器'
                />
              </div>
            </>
          )}
        </fieldset>
      </div>

      <div className='mgr-form-grid'>
        <fieldset className='mgr-fieldset'>
          <legend>时期与纸色</legend>
          <label className='mgr-field'>
            时期 eraKey
            <select value={form.eraKey} onChange={(e) => patch({ eraKey: e.target.value })}>
              {ERA_KEYS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
          <label className='mgr-field'>
            纸色 tone
            <select value={form.tone} onChange={(e) => patch({ tone: e.target.value })}>
              {TONES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className='mgr-field' style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type='checkbox' checked={form.welcome} onChange={(e) => patch({ welcome: e.target.checked })} />
            <span>
              设为首次到访欢迎信
              <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>
                新访客首次进入官网时以信封动画弹出这封公告；同时至多一封生效，冲突保存会被拒绝。
              </span>
            </span>
          </label>
        </fieldset>
        <fieldset className='mgr-fieldset'>
          <legend>发布时间（定时发布依据）</legend>
          <label className='mgr-field'>
            publishAt *
            <input type='datetime-local' value={form.publishAt} onChange={(e) => patch({ publishAt: e.target.value })} />
          </label>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 0' }}>
            发布动作后按此时间出现在对应时期板面；未到点不在公开端出现。
          </p>
        </fieldset>
      </div>
    </section>
  );
}
